// src/redux/sagas/tableAvailabilitySaga.ts
import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import type { TableAvailabilityRequest, TableAvailabilityResponse } from '../../types/tableReservation';
import {
  fetchTableAvailabilityRequest,
  fetchTableAvailabilitySuccess,
  fetchTableAvailabilityFailure,
} from '../slices/tableAvailabilitySlice';

/**
 * Saga to fetch table availability for a given date.
 * Filters out slots without available tables and calculates noTablesAvailable flag.
 * 
 * @param action - Redux action with date parameter
 */
function* fetchTableAvailabilitySaga(
  action: PayloadAction<TableAvailabilityRequest>
): Generator<unknown, void, AxiosResponse<TableAvailabilityResponse>> {
  try {
    const { date } = action.payload;
    
    // API call to fetch table availability
    const response = yield call(request<TableAvailabilityResponse>, {
      method: 'GET',
      url: ENDPOINTS.TABLE_AVAILABILITY,
      params: { date },
    });

    // Destructure response with default values
    const { availableDetails = [], time_slots = [] } = response.data || {};
    
    // Mark each time slot with availability status instead of filtering
    const slotsWithAvailability = time_slots.map((slot: any) => {
      const detail = availableDetails.find((d: any) => d.start_time === slot.start_time);
      return {
        ...slot,
        isAvailable: detail?.is_available ?? true
      };
    });

    // Determine if there are no tables available
    // If availableDetails is empty or doesn't have available_tables property, assume tables ARE available
    const noTablesAvailable = availableDetails.length > 0
      ? !availableDetails.some((detail: any) => detail.available_tables?.length > 0)
      : false;

    // Dispatch success action
    yield put(
      fetchTableAvailabilitySuccess({
        availableDetails,
        time_slots: slotsWithAvailability,
        noTablesAvailable,
      })
    );
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch table availability';
    yield put(fetchTableAvailabilityFailure(errorMessage));
  }
}

export function* watchTableAvailabilitySaga() {
  yield takeEvery(fetchTableAvailabilityRequest.type, fetchTableAvailabilitySaga);
}
